import { type FC, type MouseEvent, useState } from "react";
import {
  Box,
  ButtonBase,
  Collapse,
  Fade,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "@/shared/hooks";
import { LiveReactionEmojiType } from "@/types/graphql/site.generated";
import type { LocalReaction } from "../lib/batch";
import {
  LIVE_REACTION_EMOJI,
  LIVE_REACTION_LABEL_KEYS,
  PHRASES_ICON,
} from "../lib/reactionEmoji";

export interface LiveReactionsPanelProps {
  phrases: string[];
  /** Эмодзи платформы в порядке партнёра (Platform.liveReactionEmojis). */
  emojis: readonly LiveReactionEmojiType[];
  /** vertical: колонка у правого края кадра (десктоп), horizontal: строка под плеером (мобайл). */
  orientation: "vertical" | "horizontal";
  visible: boolean;
  /** Тихий счётчик вместо полёта (prefers-reduced-motion), null без него. */
  quietCount: number | null;
  /** Зритель свернул панель: вместо неё одна кнопка, которая её возвращает. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onReact: (reaction: LocalReaction) => void;
}

const buttonSx = {
  width: 40,
  height: 40,
  borderRadius: "50%",
  fontSize: 22,
  transition: "transform 0.15s ease",
  "&:hover": { transform: "scale(1.15)" },
  "&:active": { transform: "scale(0.95)" },
} as const;

export const LiveReactionsPanel: FC<LiveReactionsPanelProps> = ({
  phrases,
  emojis,
  orientation,
  visible,
  quietCount,
  collapsed,
  onToggleCollapsed,
  onReact,
}) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const vertical = orientation === "vertical";

  const panel = (
    <Box
      role="toolbar"
      aria-label={t("liveReactions.panel")}
      sx={
        vertical
          ? {
              // Прижата к низу кадра над сик-баром: кнопка сворачивания внизу
              // колонки, поэтому свёрнутое сердечко остаётся ровно там, где
              // был крестик, и ничего не скачет.
              position: "absolute",
              right: 8,
              bottom: 96,
              zIndex: 210,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
              p: 0.5,
              borderRadius: 6,
              backgroundColor: "rgba(0, 0, 0, 0.45)",
            }
          : {
              // Мобайл: внутри кадра, в правом нижнем углу над сик-баром.
              // Свёрнутая панель это одно сердечко, строка эмодзи выезжает
              // влево от него и не занимает место под плеером.
              position: "absolute",
              right: 8,
              bottom: 88,
              zIndex: 210,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 0.5,
              p: 0.5,
              borderRadius: 6,
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              maxWidth: "calc(100% - 16px)",
            }
      }
    >
      <Collapse
        in={!collapsed}
        orientation={vertical ? "vertical" : "horizontal"}
        timeout={220}
        unmountOnExit
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: vertical ? "column" : "row",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {emojis.map((type) => (
            <ButtonBase
              key={type}
              aria-label={t(LIVE_REACTION_LABEL_KEYS[type])}
              onClick={() => onReact({ kind: "emoji", type })}
              sx={buttonSx}
            >
              {LIVE_REACTION_EMOJI[type]}
            </ButtonBase>
          ))}
          {phrases.length > 0 && (
            <ButtonBase
              aria-label={t("liveReactions.phrases")}
              aria-haspopup="menu"
              onClick={(event: MouseEvent<HTMLElement>) =>
                setAnchor(event.currentTarget)
              }
              sx={buttonSx}
            >
              {PHRASES_ICON}
            </ButtonBase>
          )}
        </Box>
      </Collapse>
      {/* Одна кнопка на одном месте: крестик в раскрытой панели, первое
          эмодзи набора в свёрнутой, со сменой через поворот и прозрачность. */}
      <ButtonBase
        aria-label={t(collapsed ? "liveReactions.show" : "liveReactions.hide")}
        aria-expanded={!collapsed}
        onClick={onToggleCollapsed}
        sx={{
          ...buttonSx,
          position: "relative",
          color: "#fff",
        }}
      >
        <Box
          component="span"
          sx={{
            position: "absolute",
            display: "flex",
            fontSize: 18,
            transition: "opacity 0.2s ease, transform 0.2s ease",
            opacity: collapsed ? 0 : 1,
            transform: collapsed ? "rotate(-90deg) scale(0.6)" : "none",
          }}
        >
          <CloseRoundedIcon fontSize="inherit" />
        </Box>
        <Box
          component="span"
          sx={{
            position: "absolute",
            fontSize: 22,
            lineHeight: 1,
            transition: "opacity 0.2s ease, transform 0.2s ease",
            opacity: collapsed ? 1 : 0,
            transform: collapsed ? "none" : "rotate(90deg) scale(0.6)",
          }}
        >
          {LIVE_REACTION_EMOJI[emojis[0] ?? LiveReactionEmojiType.HeartType]}
        </Box>
      </ButtonBase>
      {quietCount !== null && quietCount > 0 && (
        <Typography
          variant="caption"
          aria-live="polite"
          sx={{ color: "#fff", px: 0.5 }}
        >
          {t("liveReactions.recent", { count: quietCount })}
        </Typography>
      )}
      <Menu
        anchorEl={anchor}
        // В полноэкранном плеере виден только его контейнер: меню рендерим
        // туда, иначе оно уходит в body под полноэкранный слой.
        container={() => document.fullscreenElement ?? document.body}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {phrases.map((phrase, index) => (
          <MenuItem
            key={`${index}-${phrase}`}
            onClick={() => {
              onReact({ kind: "phrase", index });
              setAnchor(null);
            }}
          >
            {phrase}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );

  // Видна вместе с контролами плеера и на десктопе, и на мобайле.
  return <Fade in={visible}>{panel}</Fade>;
};
